import React from 'react';
import { IconButton, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WebshopShopDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { PageMetaLine } from '../Styled/Styled';

type Props = {
  title: string;
  /** The meta line is left out until the restaurant has loaded. */
  restaurant?: WebshopShopDto;
  /** Sits to the right of the title from sm up, below it on phones. */
  actions?: React.ReactNode;
  /** Where the arrow before the title leads, for a page that sits one level under another. No arrow without it. */
  backTo?: string;
  /** What the arrow is called to a screen reader; defaults to "Back". */
  backLabel?: string;
};

/** Heading for the `/:restaurant/*` pages: the page title, with the restaurant beneath it. */
export const RestaurantPageHeader: React.FC<Props> = ({ title, restaurant, actions, backTo, backLabel }) => {
  const { t } = useTranslation();

  return (
    <Header>
      <TitleBlock>
        {backTo && (
          <BackButton component={Link} to={backTo} aria-label={backLabel ?? t('global.back')} size="small">
            <ArrowBack />
          </BackButton>
        )}
        <div>
          <Title variant="h1">{title}</Title>
          {restaurant && (
            <PageMetaLine>
              {restaurant.displayName} ({restaurant.shopExternalId})
            </PageMetaLine>
          )}
        </div>
      </TitleBlock>
      {actions}
    </Header>
  );
};

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

// Arrow and title on one line, the arrow centred on the title's first line rather than on the block
// (which also holds the meta line).
const TitleBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

// Pulled left by its own padding so the arrow's glyph, not its hit area, lines up with the page's
// left edge, and the title stays where it was.
const BackButton = styled(IconButton)<{ component: typeof Link; to: string }>`
  margin-left: -8px;
  margin-top: -2px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const Title = styled(Typography)`
  && {
    text-transform: uppercase;
  }
`;

export default RestaurantPageHeader;
